const productFragment = `
node {
  handle,
  featuredImage {
    url
  },
  title,
  priceRange {
    minVariantPrice {
      amount,
      currencyCode
    }
  },
  compareAtPriceRange {
    minVariantPrice {
      amount,
      currencyCode
    }
  },
  currencyCode: metafield(namespace: "custom", key: "currency_code") {
    value
    type
  }
  shortDescription: metafield(namespace: "custom", key: "short_description") {
    value
    type
  },
  tags,
  buildingExclusive: metafield(namespace: "custom", key: "building_exclusive") {
    value
    type
  }
  noPrice: metafield(namespace: "custom", key: "no_price") {
    value
    type
  }
  badges: metafield(namespace: "custom", key: "badges") {
    references(first: 20) {
      nodes {
        __typename
        ... on Metaobject {
          badge_text: field(key: "badge_text") {
            value
          }
          badge_bg_color: field(key: "badge_bg_color") {
            value
          }
        }
      }
    }
  }
  performerImages: metafield(namespace: "dev", key: "performer_images") {
    reference {
       ... on Metaobject {
        images: field(key: "images") {
          references(first: 1) {
            nodes {
              __typename
              ... on MediaImage {
                image {
                  url
                }
              }
            }
          }
        }
       }
    }
  }
  displayImageOverride: metafield(namespace: "custom", key: "product_display_image_override") {
    reference {
      ... on MediaImage {
        image {
          url
        }
      }
    }
  }
}`;

async function subscribeToPMList(formData) {
  await fetch("https://a.klaviyo.com/client/subscriptions/?company_id=WcVd7C", {
    method: "POST",
    headers: {
      revision: "2023-02-22",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      data: {
        type: "subscription",
        attributes: {
          list_id: "SXT8i3",
          custom_source: "Property Manager Sign Up",
          email: formData.get("pm-contact-email"),
          properties: Object.fromEntries(formData.entries()),
        },
      },
    }),
  });

  await fetch("https://a.klaviyo.com/client/events/?company_id=WcVd7C", {
    method: "POST",
    headers: {
      revision: "2023-09-15",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      data: {
        type: "event",
        attributes: {
          properties: Object.fromEntries(formData.entries()),
          metric: {
            data: {
              type: "metric",
              attributes: {
                name: "Property manager form submitted",
              },
            },
          },
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: formData.get("pm-contact-email"),
              },
            },
          },
        },
      },
    }),
  });
}

async function subscribeToSupplierList(formData) {
  await fetch("https://a.klaviyo.com/client/subscriptions/?company_id=WcVd7C", {
    method: "POST",
    headers: {
      revision: "2023-02-22",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      data: {
        type: "subscription",
        attributes: {
          list_id: "S7qRER",
          custom_source: " Sign Up",
          email: formData.get("supplier-contact-email"),
          properties: Object.fromEntries(formData.entries()),
        },
      },
    }),
  });

  await fetch("https://a.klaviyo.com/client/events/?company_id=WcVd7C", {
    method: "POST",
    headers: {
      revision: "2023-09-15",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      data: {
        type: "event",
        attributes: {
          properties: Object.fromEntries(formData.entries()),
          metric: {
            data: {
              type: "metric",
              attributes: {
                name: "Supplier form submitted",
              },
            },
          },
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: formData.get("supplier-contact-email"),
              },
            },
          },
        },
      },
    }),
  });
}

function getDistance(userPosition, buildingPosition) {
  const R = 3958.8; // Radius of the Earth in miles

  const userLatInRadians = userPosition.lat * (Math.PI / 180);
  const buildingLatInRadians = buildingPosition.lat * (Math.PI / 180);

  const latDifference = buildingLatInRadians - userLatInRadians;
  const lngDifference =
    (buildingPosition.lng - userPosition.lng) * (Math.PI / 180);

  return (
    2 *
    R *
    Math.asin(
      Math.sqrt(
        Math.sin(latDifference / 2) * Math.sin(latDifference / 2) +
          Math.cos(userLatInRadians) *
            Math.cos(buildingLatInRadians) *
            Math.sin(lngDifference / 2) *
            Math.sin(lngDifference / 2)
      )
    )
  );
}

function money(amount, currencyCode) {
  var price = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
  }).format(amount);
  return price.replace("CA", "");
}

function changeLocation(city, withReload = true) {
  localStorage.setItem("userCity", JSON.stringify(city));
  localStorage.setItem("collectionPagination", null);
  localStorage.setItem("userCitySaveAt", JSON.stringify(Date.now()));
  if (withReload) {
    window.location.reload();
  }
}

async function setUserLocation(isEditMode) {
  if (isEditMode) {
    let city = cities.find(
      (city) =>
        city.name.toLowerCase() === "toronto" &&
        city.regionCode.toLowerCase() === "on" &&
        city.countryCode.toLowerCase() === "ca"
    );

    if (!city) {
      city = cities[0];
    }

    changeLocation(city);
  } else {
    const {
      location: { lat, lng },
    } = await fetch(
      `https://www.googleapis.com/geolocation/v1/geolocate?key=${window.googleAPIKey}`,
      { method: "POST" }
    ).then((response) => response.json());

    const geocodingData = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${window.googleAPIKey}&language=en`,
      { method: "POST" }
    ).then((response) => response.json());
    const userLocation = {
      city: geocodingData.results[0].address_components.find((item) =>
        item.types.includes("locality")
      ).long_name,
      region: geocodingData.results[0].address_components.find((item) =>
        item.types.includes("administrative_area_level_1")
      ).short_name,
      countryCode: geocodingData.results[0].address_components.find((item) =>
        item.types.includes("country")
      ).short_name,
      lat,
      lng,
    };

    const userCity = cities.find(
      (city) =>
        city.name.toLowerCase() === userLocation.city.toLowerCase() &&
        city.regionCode.toLowerCase() === userLocation.region.toLowerCase() &&
        city.countryCode.toLowerCase() ===
          userLocation.countryCode.toLowerCase()
    );

    if (userCity) {
      // Alpine.store('global').userCity = userCity;
      // window.location.reload();
      changeLocation(userCity);
      console.log("user city was found.");
    } else {
      const nearestCity = cities.reduce((previous, current) =>
        getDistance(userLocation, previous) < getDistance(userLocation, current)
          ? previous
          : current
      );

      if (getDistance(userLocation, nearestCity) > 50) {
        localStorage.setItem("userCity", null);

        if (
          ![
            "/pages/find-your-city",
            "/pages/contact",
            "/pages/frequently-asked-questions",
            "/pages/privacy-policy",
            "/pages/terms-conditions",
            "/account/login",
            "/account/register",
            "/account/login#recover",
            "/pages/supplier-sign-up",
            "/pages/user-sign-up",
            "/pages/property-manager-sign-up",
          ].includes(window.location.pathname) &&
          !isCityPage
        ) {
          window.location.href = "/pages/find-your-city";
        }
        console.log("nearest city wasn`t found.");
      } else {
        // Alpine.store('global').userCity = nearestCity;
        // window.location.reload();
        changeLocation(nearestCity);
        console.log("nearest city was found.");
      }
    }
  }
}

// async function getCollectionByHandle(handle, customerBuilding) {
//   const response = await fetch(
//     `${shopUrl}/api/2023-01/graphql.json`,
//     {
//       method: 'POST',
//       headers: {
//         'X-Shopify-Storefront-Access-Token': '55cb77a0d00bbd6c13230f00824dab33',
//         'Content-Type': 'application/json'
//       },
//       body: JSON.stringify({
//         query: `{ collection(handle: "${handle}") {
//           handle
//           products(first: 20) {
//             edges {
//               node {
//                 handle,
//                 featuredImage {
//                   url
//                 },
//                 title,
//                 priceRange {
//                   minVariantPrice {
//                     amount,
//                     currencyCode
//                   }
//                 },
//                 compareAtPriceRange {
//                   minVariantPrice {
//                     amount,
//                     currencyCode
//                   }
//                 },
//                 currencyCode: metafield(namespace: "custom", key: "currency_code") {
//                   value
//                   type
//                 }
//                 shortDescription: metafield(namespace: "custom", key: "short_description") {
//                   value
//                   type
//                 },
//                 tags,
//                 buildingExclusive: metafield(namespace: "custom", key: "building_exclusive") {
//                   value
//                   type
//                 }
//                 noPrice: metafield(namespace: "custom", key: "no_price") {
//                   value
//                   type
//                 }
//                 badges: metafield(namespace: "custom", key: "badges") {
// 									references(first: 20) {
// 										nodes {
// 											__typename
// 											... on Metaobject {
// 												badge_text: field(key: "badge_text") {
// 													value
// 												}
// 												badge_bg_color: field(key: "badge_bg_color") {
// 													value
// 												}
// 											}
// 										}
// 									}
//                 }
//               }
//             }
//           }
//         }}`
//       })
//     }
//   );
//   const data = await response.json();

//   if (!data.data.collection) {
//     return null;
//   }

//   return {
//     ...data.data.collection,
//     products: data.data.collection.products.edges
//       .filter(
//         ({ node }) =>
//           node.buildingExclusive?.value != 'true' ||
//           node.buildingExclusive?.value == 'true' &&
//           customerBuilding && node.tags.includes(customerBuilding)
//       )
//       .map(({ node }) => ({
//         compareAtPrice: node.compareAtPriceRange.minVariantPrice,
//         shortDescription: node.shortDescription?.value || "",
//         featuredImage: node.featuredImage.url,
//         handle: node.handle,
//         price: node.priceRange.minVariantPrice,
//         currencyCode: node.currencyCode?.value || null,
//         title: node.title,
//         noPrice: node.noPrice?.value === 'true',
//         badges: node.badges?.references.nodes.map(({badge_bg_color, badge_text}) => ({
//           badge_text: badge_text.value,
//           badge_bg_color: badge_bg_color.value
//         }))
//       }))
//   };
// }

function nodeToProduct({ node }) {
  let pro = {
    compareAtPrice: node.compareAtPriceRange.minVariantPrice,
    shortDescription: node.shortDescription?.value || "",
    featuredImage: node.featuredImage?.url || null,
    handle: node.handle,
    price: node.priceRange.minVariantPrice,
    currencyCode: node.currencyCode?.value || null,
    title: node.title,
    noPrice: node.noPrice?.value === "true",
    badges: node.badges?.references.nodes.map(
      ({ badge_bg_color, badge_text }) => ({
        badge_text: badge_text.value,
        badge_bg_color: badge_bg_color.value,
      })
    ),
    performerImages:
      node.performerImages?.reference.images.references.nodes.map(
        (image) => image.image.url
      ),
  };

  if (pro.performerImages && pro.performerImages.length > 0) {
    pro.featuredImage = pro.performerImages[0];
  }

  if (node.displayImageOverride?.reference?.image?.url) {
    pro.featuredImage = node.displayImageOverride.reference.image.url;
  }

  if (pro.featuredImage === null) {
    pro.featuredImage =
      "https://tenexperks.com/cdn/shopifycloud/shopify/assets/no-image-2048-5e88c1b20e087fb7bbe9a3771824e743c244f437e4f8ba93bbf7b11b53f7824c.gif";
  }

  return pro;
}

async function getCollectionByHandle(handle, options) {
  const {
    filters,
    withPagination,
    optionalFilters,
    productsPerPage,
    after,
    optionalAfter,
  } = options;

  const response = await fetch(`${shopUrl}/api/2023-07/graphql.json`, {
    method: "POST",
    headers: {
      "X-Shopify-Storefront-Access-Token": "55cb77a0d00bbd6c13230f00824dab33",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      query: `query (
          $handle: String!,
          $filters: [ProductFilter!],
          $after: String,
          $first: Int${
            optionalFilters && optionalAfter !== "-"
              ? `, 
          $optionalFilters: [ProductFilter!],
          $optionalAfter: String`
              : ""
          }
        ) {
          collection(handle: $handle) {
            handle
            products: products(first: $first, after: $after, filters: $filters, sortKey: COLLECTION_DEFAULT) {
              edges {
                ${productFragment}
                ${withPagination ? "cursor" : ""}
              }
              ${
                withPagination
                  ? `pageInfo {
                hasNextPage
                hasPreviousPage
              }`
                  : ""
              }
            }

            ${
              optionalFilters && optionalAfter !== "-"
                ? `optionalProducts: products(first: $first, after: $optionalAfter, filters: $optionalFilters, sortKey: COLLECTION_DEFAULT) {
              edges {
                ${productFragment}
                ${withPagination ? "cursor" : ""}
              }
              ${
                withPagination
                  ? `pageInfo {
                hasNextPage
                hasPreviousPage
              }`
                  : ""
              }
            }`
                : ""
            }
          }
        }`,
      variables: {
        handle,
        filters,
        after,
        first: productsPerPage,
        optionalFilters,
        optionalAfter,
      },
    }),
  });
  const data = await response.json();

  if (!data.data.collection) {
    return null;
  }

  const optionalPageInfo = data.data.collection?.optionalProducts?.pageInfo;
  const pageInfo = data.data.collection?.products?.pageInfo;

  const optionalProducts = data.data.collection?.optionalProducts?.edges || [];
  const products =
    data.data.collection?.products?.edges?.slice(
      0,
      productsPerPage - optionalProducts.length
    ) || [];

  return {
    handle: data.data.collection.handle,
    products: [
      ...(optionalProducts?.map(nodeToProduct) || []),
      ...(products?.map(nodeToProduct) || []),
    ],
    ...(withPagination
      ? {
          pageInfo: {
            after:
              pageInfo?.hasNextPage ||
              data.data.collection?.products?.edges.length > products?.length
                ? products?.[products?.length - 1]?.cursor
                : "-",
            optionalAfter: optionalPageInfo?.hasNextPage
              ? optionalProducts?.[optionalProducts?.length - 1]?.cursor
              : "-",
          },
        }
      : {}),
  };
}

// async function getCollectionByHandle(handle, options) {
//   const {
//     filters,
//     before = null,
//     after = null,
//     first = null,
//     last = null,
//     withPagination,
//     optionalFilters,
//     optionalBefore = null,
//     optionalAfter = null,
//   } = options;

//   const optionalLast = optionalBefore ? last : null;
//   const optionalFirst = last && !optionalBefore ? last : first;
//   const optionalReverse = last && !optionalBefore ? false : true;

//   console.log(options)

//   const response = await fetch(
//     `${shopUrl}/api/2023-07/graphql.json`,
//     {
//       method: 'POST',
//       headers: {
//         'X-Shopify-Storefront-Access-Token': '55cb77a0d00bbd6c13230f00824dab33',
//         'Content-Type': 'application/json'
//       },
//       body: JSON.stringify({
//         query: `query (
//           $handle: String!,
//           $filters: [ProductFilter!],
//           $before: String,
//           $after: String,
//           $first: Int,
//           $last: Int ${optionalFilters? `,
//           $optionalFilters: [ProductFilter!],
//           $optionalBefore: String,
//           $optionalAfter: String,
//           $optionalLast: Int,
//           $optionalFirst: Int,
//           $optionalReverse: Boolean`: ""}
//         ) {
//           collection(handle: $handle) {
//             handle
//             products: products(first: $first, last: $last, before: $before, after: $after, filters: $filters, sortKey: COLLECTION_DEFAULT) {
//               edges {
//                 ${productFragment}
//                 ${withPagination ? "cursor" : ""}
//               }
//               ${withPagination ? `pageInfo {
//                 hasNextPage
//                 hasPreviousPage
//                 startCursor
//                 endCursor
//               }` : ""}
//             }

//             ${optionalFilters ?
//             `optionalProducts: products(first: $optionalFirst, reverse: $optionalReverse, last: $optionalLast, before: $optionalBefore, after: $optionalAfter, filters: $optionalFilters, sortKey: COLLECTION_DEFAULT) {
//               edges {
//                 ${productFragment}
//                 ${withPagination ? "cursor" : ""}
//               }
//               ${withPagination ? `pageInfo {
//                 hasNextPage
//                 hasPreviousPage
//               }` : ""}
//             }` : ""}
//           }
//         }`,
//         variables: { handle, filters, before, after, first, last, optionalFilters, optionalBefore, optionalAfter, optionalLast, optionalFirst, optionalReverse }
//       })
//     }
//   );
//   const data = await response.json();

//   if (!data.data.collection) {
//     return null;
//   }

//   const productsPerPage = first || last;
//   const optionalPageInfo = data.data.collection?.optionalProducts?.pageInfo;
//   const pageInfo = data.data.collection?.products?.pageInfo;
//   let products;

//   console.log("pageInfo", pageInfo);

//   if (first) {
//     optionalProducts = data.data.collection?.optionalProducts?.edges || [];
//     if (after && !optionalAfter) {
//       optionalProducts = [];
//     }
//     products = data.data.collection?.products?.edges.slice(0, productsPerPage - optionalProducts.length) || [];
//   }

//   if (last) {
//     products = data.data.collection?.products?.edges || [];
//     if (optionalBefore && !before) {
//       products = [];
//     }
//     optionalProducts = data.data.collection?.optionalProducts?.edges.slice(0, productsPerPage - products.length) || [];
//   }

//   console.log(products, optionalProducts)

//   console.log({
//     before: pageInfo?.hasPreviousPage || data.data.collection?.products?.edges.length > products?.length
//       ? products?.[0]
//       : null,
//     optionalBefore: (optionalPageInfo?.hasPreviousPage || optionalProducts?.length) && !before
//       ? optionalProducts?.[0]
//       : null,
//   });

//   console.log(products, optionalProducts)

//   return {
//     handle: data.data.collection.handle,
//     products: [
//       ...optionalProducts?.map(nodeToProduct) || [],
//       ...products?.map(nodeToProduct) || []
//     ],
//     ...(withPagination
//       ? {
//         pageInfo: {
//           hasNextPage: (optionalPageInfo?.hasNextPage && optionalProducts?.length) || pageInfo?.hasNextPage || (data.data.collection?.products?.edges.length > products?.length),

//           after: pageInfo?.hasNextPage || data.data.collection?.products?.edges.length > products?.length
//             ? products?.[products?.length - 1]?.cursor
//             : null,
//           optionalAfter: (optionalPageInfo?.hasNextPage || optionalProducts?.length) && !after
//             ? optionalProducts?.[optionalProducts?.length - 1]?.cursor
//             : null,

//           hasPreviousPage: (pageInfo?.hasPreviousPage && products?.length) || optionalPageInfo?.hasPreviousPage || (data.data.collection?.optionalProducts?.edges.length > optionalProducts?.length),

//           before: pageInfo?.hasPreviousPage || data.data.collection?.products?.edges.length > products?.length
//             ? products?.[0]?.cursor
//             : null,
//           optionalBefore: (optionalPageInfo?.hasPreviousPage || optionalProducts?.length) && !before
//             ? optionalProducts?.[0]?.cursor
//             : null,
//         }
//       } : {}
//     )
//   };
// }

async function getBannerSection(handle) {
  const response = await fetch(`${shopUrl}/api/2023-01/graphql.json`, {
    method: "POST",
    headers: {
      "X-Shopify-Storefront-Access-Token": "55cb77a0d00bbd6c13230f00824dab33",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      query: `fragment banner on Metaobject {
            image: field(key: "image") {
              reference {
                ... on MediaImage {
                  image {
                    url
                  }
                }
              }
            }
            subtitle: field(key: "subtitle") {
              value
            }
            title: field(key: "title") {
              value
            }
            description: field(key: "description") {
              value
            }
            buttonTitle: field(key: "button_title") {
              value
            }
            buttonLink: field(key: "button_link") {
              value
            }
            darkenImage: field(key: "darken_image") {
              value
            }
            bannerTextColor: field(key: "banner_text_color") {
              value
            }
          }

          {
            metaobject(handle: {type: "banner_section", handle: "${handle}" }) {
              handle
              id
              sectionTitle: field(key: "section_title")  {
                value
              }
              staticBanner: field(key: "static_banner") {
                reference {
                  ...banner
                }
              }
              bannersList: field(key: "banners_list") {
                references(first: 10) {
                  nodes {
                    ...banner
                  }
                }
              }
              viewAllUrl: field(key: "view_all_url") {
                value
              }
            }
          }`,
    }),
  });

  const data = await response.json();

  if (!data?.data?.metaobject) {
    return null;
  }

  return {
    bannersList: data.data.metaobject.bannersList.references.nodes.map(
      (item) => ({
        bannerTextColor: item.bannerTextColor?.value || "#ffffff",
        buttonLink: item.buttonLink?.value || "",
        buttonTitle: item.buttonTitle?.value || "",
        darkenImage: Boolean(item.darkenImage?.value),
        description: item.description?.value || "",
        image: item.image.reference.image.url,
        subtitle: item.subtitle?.value || "",
        title: item.title?.value || "",
      })
    ),
    sectionTitle: data.data.metaobject.sectionTitle?.value || "",
    staticBanner: {
      bannerTextColor:
        data.data.metaobject.staticBanner.reference.bannerTextColor?.value ||
        "#ffffff",
      buttonLink:
        data.data.metaobject.staticBanner.reference.buttonLink?.value || "",
      buttonTitle:
        data.data.metaobject.staticBanner.reference.buttonTitle?.value || "",
      darkenImage: Boolean(
        data.data.metaobject.staticBanner.reference.darkenImage?.value
      ),
      description:
        data.data.metaobject.staticBanner.reference.description?.value || "",
      image:
        data.data.metaobject.staticBanner.reference.image.reference.image.url,
      subtitle:
        data.data.metaobject.staticBanner.reference.subtitle?.value || "",
      title: data.data.metaobject.staticBanner.reference.title?.value || "",
    },
    viewAllUrl: data.data.metaobject.viewAllUrl?.value || "",
  };
}

async function getBanner(handle) {
  const response = await fetch(`${shopUrl}/api/2023-01/graphql.json`, {
    method: "POST",
    headers: {
      "X-Shopify-Storefront-Access-Token": "55cb77a0d00bbd6c13230f00824dab33",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      query: `{
            metaobject(handle: {type: "banner", handle: "${handle}" }) {
              handle
              id
              image: field(key: "image") {
                reference {
                  ... on MediaImage {
                    image {
                      url
                    }
                  }
                }
              }
              subtitle: field(key: "subtitle") {
                value
              }
              title: field(key: "title") {
                value
              }
              description: field(key: "description") {
                value
              }
              buttonTitle: field(key: "button_title") {
                value
              }
              buttonLink: field(key: "button_link") {
                value
              }
              darkenImage: field(key: "darken_image") {
                value
              }
              bannerTextColor: field(key: "banner_text_color") {
                value
              }
            }
          }`,
    }),
  });

  const data = await response.json();

  if (!data?.data?.metaobject) {
    return null;
  }

  return {
    bannerTextColor: data.data.metaobject.bannerTextColor?.value || "#ffffff",
    buttonLink: data.data.metaobject.buttonLink?.value || "",
    buttonTitle: data.data.metaobject.buttonTitle?.value || "",
    darkenImage: Boolean(data.data.metaobject.darkenImage?.value),
    description: data.data.metaobject.description?.value || "",
    image: data.data.metaobject.image.reference.image.url,
    subtitle: data.data.metaobject.subtitle?.value || "",
    title: data.data.metaobject.title?.value || "",
  };
}

function addCustomBadges(badges, badgesRef) {
  if (badges?.length) {
    for (badge of badges) {
      const badgeHTML = document.createElement("div");
      // don't  use setHTML
      badgeHTML.innerHTML = `<div class="rounded-full px-2.5 py-0.5 text-sm text-white" style="background-color: ${badge.badge_bg_color}">${badge.badge_text}</div>`;

      // badgeHTML.setHTML(
      //   `<div class="rounded-full px-2.5 py-0.5 text-sm text-white" style="background-color: ${badge.badge_bg_color}">${badge.badge_text}</div>`
      // );
      badgesRef.append(badgeHTML);
    }
  }
}

function getAcessibleAreasForUser() {
  const userCity = Alpine.store("global").userCity;
  return [
    `${userCity.countryCode}-${userCity.regionCode}-${userCity.name.replace(
      " ",
      "-"
    )}`.toLowerCase(),
    `${userCity.countryCode}-${userCity.regionCode}-${userCity.name.replace(
      " ",
      ""
    )}`.toLowerCase(),
    `${userCity.countryCode}-${userCity.regionCode}-${userCity.name}`.toLowerCase(),
    `${userCity.countryCode}-${userCity.regionCode}`.toLowerCase(),
    `${userCity.countryCode}`.toLowerCase(),
  ];
}

function geofilter(collection) {
  if (!collection?.products?.length) {
    return null;
  }

  const accessibleAreasForUser = getAcessibleAreasForUser();

  return collection.products.filter(
    (product) =>
      product.tags.every((tag) => !tag.toLowerCase().startsWith("city:")) ||
      (product.tags.find((tag) =>
        accessibleAreasForUser
          .map((area) => `city:${area}`)
          .includes(tag.toLowerCase())
      ) &&
        (product.tags.every(
          (tag) => !tag.toLowerCase().startsWith("exclude:")
        ) ||
          !product.tags.find((tag) =>
            accessibleAreasForUser
              .map((area) => `exclude:${area}-${collection.handle}`)
              .includes(tag.toLowerCase())
          )))
  );
}

function pmFormHandler() {
  return {
    showSuccessMessage: false,

    submitForm() {
      const formData = new FormData(this.$refs.form);

      subscribeToPMList(formData)
        .then(() => {
          this.showSuccessMessage = true;

          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        })
        .catch((error) => {
          console.error("There was an error processing the form:", error);
          // Handle errors here
        });
    },
  };
}

function supplierFormHandler() {
  return {
    showSuccessMessage: false,

    submitForm() {
      const formData = new FormData(this.$refs.form);

      subscribeToSupplierList(formData)
        .then(() => {
          this.showSuccessMessage = true;

          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        })
        .catch((error) => {
          console.error("There was an error processing the form:", error);
          // Handle errors here
        });
    },
  };
}

function initcalenderPicker(onDateSelect) {
  const calendar = new AirDatepicker("#popup-calender", {
    inline: true,
    dateFormat: "YYYY-MM-dd",
    range: true,
    minView: "days",
    minDate: new Date(),
    // selectedDates: [new Date()],
    fixedHeight: true,
    locale: {
      days: [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      daysShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      daysMin: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
      months: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      monthsShort: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      today: "Today",
      clear: "Clear",
      firstDay: 0,
    },
    isMobile: true,
    onSelect: onDateSelect,
  });

  window.calendar = calendar;
  return calendar;
}

window.Cache = {};

async function getEventsByPerformer(query, calender = false) {
  const parmas = {
    perPage: 40,
    page: query.page || 1,
    performerId: query.performerId,
  };
  if (Alpine.store("global").userCity.vivid_region_id) {
    parmas.regionId = parseInt(Alpine.store("global").userCity.vivid_region_id);
  }
  if (calender) {
    if (calendar.selectedDates.length > 0) {
      var Dates = calendar.selectedDates;
      // console.log(Dates);
      parmas.dateStart = dayjs(Dates[0]).format("YYYY-MM-DD");
      parmas.dateEnd = dayjs(Dates[1]).format("YYYY-MM-DD");
    }
  }
  // join params with &

  var key = Object.keys(parmas).map((key) => `${key}=${parmas[key]}`);
  key = key.join("&");

  if (window.Cache[key]) {
    return window.Cache[key];
  } else {
    const response = await fetch("https://app.tenexperks.com/api/events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(parmas),
    });
    const data = await response.json();
    window.Cache[key] = data;
    console.log(data);
    return data;
  }
}

function venueAddress(venue) {
  return `${venue.name} • ${venue.address.city}, ${venue.address.stateCode}`;
}

function calendarActionList() {
  return [
    {
      label: "Any Dates",
      isActive: true,
    },
    {
      label: "This Weekend",
      isActive: false,
    },
    {
      label: "This Week",
      isActive: false,
    },
    {
      label: "This Month",
      isActive: false,
    },
    {
      label: "Next 7 Days",
      isActive: false,
    },
    {
      label: "Next 30 Days",
      isActive: false,
    },
    {
      label: "Next 60 Days",
      isActive: false,
    },
  ];
}

function getDateRange(index) {
  const daterange = [];
  switch (index) {
    case 1:
      // this weekend
      daterange.push(dayjs().startOf("week").add(5, "days"));
      daterange.push(dayjs().startOf("week").add(7, "days"));
      break;
    case 2:
      // current week
      daterange.push(dayjs().startOf("week"));
      daterange.push(dayjs().endOf("week"));
      break;
    case 3:
      // current month
      daterange.push(dayjs().startOf("month"));
      daterange.push(dayjs().endOf("month"));
      break;
    case 4:
      // next 7 days
      daterange.push(dayjs());
      daterange.push(dayjs().add(7, "days"));
      break;
    case 5:
      // next 30 days
      daterange.push(dayjs());
      daterange.push(dayjs().add(30, "days"));
      break;
    case 6:
      // next 60 days
      daterange.push(dayjs());
      daterange.push(dayjs().add(60, "days"));
      break;
  }
  if (daterange.length > 0 && index <= 3) {
    if (daterange[0].isBefore(dayjs())) {
      daterange[0] = dayjs();
    }
  }

  return daterange;
}

function removeInRageClass(calendar) {
  calendar.$content
    .querySelectorAll(".air-datepicker-cell.-in-range-")
    .forEach((el) => {
      el.classList.remove("-in-range-");
    });
}
